import { useDebounceFn } from 'ahooks'
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import SearchIcon from '@/assets/image-view/search.svg'

import { ImageKeywordError, parseImageKeywords } from '../utils/imageKeywords'

interface Props {
  value: string[]
  onChange: (keywords: string[]) => void
}

export function ImageKeywordSearch({ value, onChange }: Props) {
  const { t } = useTranslation()
  const id = useId()
  const [draft, setDraft] = useState(value.join(' '))
  const [error, setError] = useState<ImageKeywordError>()
  const composing = useRef(false)
  const submitKeywords = (input: string) => {
    const result = parseImageKeywords(input)
    setError(result.error)
    if (!result.error && JSON.stringify(result.keywords) !== JSON.stringify(value)) {
      onChange(result.keywords)
    }
  }
  const { run: scheduleSearch, cancel: cancelSearch } = useDebounceFn(submitKeywords, { wait: 400 })

  useEffect(() => {
    cancelSearch()
    // Keep separators while typing so a pause after a space does not join two keywords.
    setDraft((current) =>
      JSON.stringify(parseImageKeywords(current).keywords) === JSON.stringify(value)
        ? current
        : value.join(' '),
    )
    setError(undefined)
  }, [value, cancelSearch])

  return (
    <div
      role="search"
      className="w-[200px] shrink-0"
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return
        event.preventDefault()
        event.stopPropagation()
        if (composing.current || event.nativeEvent.isComposing) return
        cancelSearch()
        submitKeywords(draft)
      }}
    >
      <div className="flex h-11 items-center gap-2 rounded-lg border border-neutral-200 bg-panel-white px-3 focus-within:border-secondary">
        <span
          aria-hidden="true"
          className="size-6 shrink-0 bg-current text-primary"
          style={{
            mask: `url(${SearchIcon}) center / contain no-repeat`,
            WebkitMask: `url(${SearchIcon}) center / contain no-repeat`,
          }}
        />
        <input
          type="search"
          value={draft}
          aria-label={t('create.imageView.keywordSearch')}
          placeholder={t('create.imageView.searchKeywords')}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          className="min-w-0 flex-1 bg-transparent t-sm text-primary outline-none placeholder:text-tertiary [&::-webkit-search-cancel-button]:appearance-none"
          onChange={(event) => {
            setDraft(event.target.value)
            setError(undefined)
            if (!composing.current) scheduleSearch(event.target.value)
          }}
          onCompositionStart={() => {
            composing.current = true
            cancelSearch()
          }}
          onCompositionEnd={(event) => {
            composing.current = false
            setDraft(event.currentTarget.value)
            scheduleSearch(event.currentTarget.value)
          }}
        />
        {(draft || value.length > 0) && (
          <button
            type="button"
            aria-label={t('create.imageView.clearKeywords')}
            className="t-sm text-secondary"
            onClick={() => {
              cancelSearch()
              setDraft('')
              setError(undefined)
              if (value.length > 0) onChange([])
            }}
          >
            ×
          </button>
        )}
      </div>
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 t-xs text-error">
          {t(`create.imageView.keywordErrors.${error}`)}
        </p>
      )}
    </div>
  )
}
